import React, { useContext, useEffect, useState, useCallback } from "react";
import { Box, Flex, Grid, Input, Label, Select, Switch } from "theme-ui";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { themevals } from "theme/themevals";
import {
  getGlobalExpirationSettings,
  getExpirationSettings,
  updateGlobalExpirationSettings,
  getClientExpirationOverrides,
  setClientExpirationOverrides,
  listClientExpirationOverrides,
  runExpirationJob,
  sendExpirationDigest,
  populateRetroactive,
  closeLegacyRecords,
} from "api/QuoteExpiration";
import { getClients } from "api/Clients";

const OVERRIDABLE_FIELDS = [
  { key: "quoteExpirationDays", label: "Quote Expiration (days)" },
  { key: "rfqExpirationDays", label: "RFQ Expiration (days)" },
  { key: "expirationWarningDays", label: "Warning Window (days)" },
  { key: "maxRegenerations", label: "Max Regenerations" },
  { key: "regenerationExpirationDays", label: "Regeneration Expiration (days)" },
  { key: "rfqExtensionDays", label: "RFQ Extension (days)" },
  { key: "maxRfqExtensions", label: "Max RFQ Extensions" },
  { key: "highValueThreshold", label: "High Value Threshold ($)" },
  { key: "priceLockDepositPercent", label: "Price Lock Deposit (%)" },
  { key: "priceLockMaxDays", label: "Price Lock Max (days)" },
];

const FIELD_DEFS = [
  ...OVERRIDABLE_FIELDS,
  { key: "retroactiveGraceDays", label: "Retroactive Grace (days)" },
];

const NOTIFICATION_DEFAULTS = [
  {
    key: "defaultQuoteExpirationFrequency",
    label: "Quote Expiration Alerts",
    description: "Default frequency for quote expiration alerts sent to users.",
  },
  {
    key: "defaultRfqExpirationFrequency",
    label: "RFQ Expiration Alerts",
    description: "Default frequency for RFQ expiration alerts sent to users.",
  },
  {
    key: "defaultPriceLockFrequency",
    label: "Price Lock Alerts",
    description: "Default frequency for price lock expiration alerts sent to users.",
  },
  {
    key: "defaultDigestFrequency",
    label: "Expiration Summary",
    description: "Default frequency for the scheduled expiration digest sent to users.",
  },
];

const FREQUENCY_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "immediate", label: "As they happen" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DIGEST_FREQUENCY_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
];

const FIELD_DESCRIPTIONS: Record<string, string> = {
  quoteExpirationDays: "Days until an active quote expires",
  rfqExpirationDays: "Days until an un-quoted RFQ expires",
  expirationWarningDays: "Days before expiration to show warnings and nav badges",
  maxRegenerations: "How many times an expired quote can be regenerated",
  regenerationExpirationDays: "Expiration period for regenerated quotes",
  rfqExtensionDays: "Days added when a client extends an RFQ",
  maxRfqExtensions: "Number of times a client can extend an RFQ",
  highValueThreshold: "Quotes above this get individual notifications",
  priceLockDepositPercent: "Deposit percentage for price lock",
  priceLockMaxDays: "Maximum price lock duration",
  retroactiveGraceDays: "Grace period when populating existing open records",
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode; description?: string }> = ({ title, children, description }) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      borderRadius: "6px",
      border: "1px solid",
      borderColor: themevals.colors.inputOutline,
      boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
      marginBottom: "24px",
      padding: "24px",
    }}
  >
    <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "16px", fontWeight: 600, color: "base_800", marginBottom: "4px" }}>
      {title}
    </Box>
    {description && (
      <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "base_500", marginBottom: "20px" }}>
        {description}
      </Box>
    )}
    {children}
  </Box>
);

const SettingsField: React.FC<{
  fieldKey: string;
  label: string;
  value: any;
  onChange: (key: string, value: any) => void;
  description?: string;
}> = ({ fieldKey, label, value, onChange, description }) => (
  <Box>
    <Label sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "4px", display: "block" }}>
      {label}
    </Label>
    {description && (
      <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginBottom: "6px" }}>{description}</Box>
    )}
    <Input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(fieldKey, e.target.value)}
      sx={{ fontFamily: "Poppins", fontSize: "14px", border: "1px solid", borderColor: "base_300", borderRadius: "6px", padding: "8px 12px", backgroundColor: "#fff" }}
      min={0}
    />
  </Box>
);

/**
 * Chrome for the page. Top-level on purpose: a component defined inside
 * ExpirationSettings would be a new type every render and remount every
 * field on each keystroke.
 */
function ExpirationShell({ embedded, children, subtitle }: { embedded: boolean; children: React.ReactNode; subtitle?: string }) {
  return embedded ? (
    <Box>
      {subtitle && <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: "base_500", marginBottom: "16px" }}>{subtitle} These save separately from the rest of Company Settings.</Box>}
      {children}
    </Box>
  ) : (
    <>
      <Header />
      <Page title="Expiration Settings" subtitle={subtitle}>{children}</Page>
    </>
  );
}

/**
 * Rendered two ways: as the Company Settings > Expiration tab
 * (`embedded`, no Header/Page chrome - the tab supplies it) or, for old
 * links, standalone. `/expiration-settings` now redirects to the tab.
 */
export default function ExpirationSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useContext<any>(UserContext);
  const isSuper = user?.accessLevel === "isSuper";
  const isAdmin = isSuper || user?.accessLevel === "isCompany";
  const toast = useToast();

  const [settings, setSettings] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningJob, setRunningJob] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [populateMaxAge, setPopulateMaxAge] = useState<string>("90");
  const [closingLegacy, setClosingLegacy] = useState(false);
  const [legacyOlderThan, setLegacyOlderThan] = useState<string>("90");
  const [dirty, setDirty] = useState(false);

  // Per-client override state
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientResults, setClientResults] = useState<any[]>([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [clientOverrides, setClientOverrides] = useState<any>({});
  const [clientDirty, setClientDirty] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  // Summary list of all clients that already have overrides, shown as collapsed
  // rows above the search so the page does not grow unbounded and the admin can
  // see/jump to any configured client. Clicking a row selects that client,
  // expanding the editor below.
  const [overrideClients, setOverrideClients] = useState<any[]>([]);

  const refreshOverrideClients = async () => {
    const res = await listClientExpirationOverrides();
    setOverrideClients(Array.isArray(res?.clients) ? res.clients : []);
  };

  useEffect(() => {
    const load = async () => {
      const data = isAdmin ? await getGlobalExpirationSettings() : await getExpirationSettings();
      setSettings(data);
      setFormValues({ ...data });
      setLoading(false);
    };
    load();
  }, [isAdmin]);

  const handleChange = (key: string, value: any) => {
    setFormValues((prev: any) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload: any = {};
    for (const field of FIELD_DEFS) {
      if (formValues[field.key] !== undefined) {
        payload[field.key] = Number(formValues[field.key]);
      }
    }
    payload.isEnabled = !!formValues.isEnabled;
    // Notification defaults (string fields)
    for (const notif of NOTIFICATION_DEFAULTS) {
      if (formValues[notif.key] !== undefined) {
        payload[notif.key] = formValues[notif.key];
      }
    }
    const res = await updateGlobalExpirationSettings(payload);
    if (res?.id || res?.isEnabled !== undefined) {
      toast.add("Settings saved", "var(--theme-ui-colors-success)");
      setSettings(res);
      setDirty(false);
    } else {
      toast.add(res?.message || "Failed to save settings", "var(--theme-ui-colors-danger)");
    }
    setSaving(false);
  };

  const handleRunJob = async () => {
    setRunningJob(true);
    const res = await runExpirationJob();
    if (res?.success !== false) {
      toast.add(
        "Expiration job complete: " +
          (res.expiredQuotes || 0) + " quotes, " +
          (res.expiredRfqs || 0) + " RFQs, " +
          (res.expiredPriceLocks || 0) + " price locks expired",
        "var(--theme-ui-colors-success)"
      );
    } else {
      toast.add(res?.message || "Failed to run expiration job", "var(--theme-ui-colors-danger)");
    }
    setRunningJob(false);
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    const res = await sendExpirationDigest();
    if (res?.success !== false) {
      toast.add(
        "Digest sent: " + (res.companiesSent || 0) + " companies, " + (res.companiesSkipped || 0) + " skipped, " + (res.errors || 0) + " errors",
        "var(--theme-ui-colors-success)"
      );
    } else {
      toast.add(res?.message || "Failed to send digest", "var(--theme-ui-colors-danger)");
    }
    setSendingDigest(false);
  };

  const handlePopulate = async (mode: string) => {
    setPopulating(true);
    const maxAge = populateMaxAge ? parseInt(populateMaxAge, 10) : undefined;
    const res = await populateRetroactive(mode, undefined, maxAge || undefined);
    if (res?.success !== false) {
      toast.add(
        "Populated: " + (res.quotesUpdated || 0) + " quotes, " + (res.rfqsUpdated || 0) + " RFQs (" + mode + (maxAge ? ", last " + maxAge + " days" : ", all records") + ")",
        "var(--theme-ui-colors-success)"
      );
    } else {
      toast.add(res?.message || "Failed to populate retroactive dates", "var(--theme-ui-colors-danger)");
    }
    setPopulating(false);
  };

  const handleCloseLegacy = async () => {
    const days = legacyOlderThan ? parseInt(legacyOlderThan, 10) : 90;
    if (!window.confirm("This will close all open quotes and RFQs older than " + days + " days. They will be marked as expired with a legacy cleanup note and will NOT appear as action items. Continue?")) return;
    setClosingLegacy(true);
    const res = await closeLegacyRecords(days);
    if (res?.success !== false) {
      toast.add(
        "Legacy cleanup: " + (res.quotesClosed || 0) + " quotes, " + (res.rfqsClosed || 0) + " RFQs closed",
        "var(--theme-ui-colors-success)"
      );
    } else {
      toast.add(res?.message || "Failed to close legacy records", "var(--theme-ui-colors-danger)");
    }
    setClosingLegacy(false);
  };

  // Client search with debounce
  const searchClients = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setClientResults([]);
      return;
    }
    setClientSearching(true);
    try {
      const res = await getClients({ query: { search: term, filters: { type: 2 } } });
      const items = Array.isArray(res?.items || res) ? (res?.items || res) : [];
      setClientResults(items.slice(0, 20));
    } catch (e) {
      setClientResults([]);
    }
    setClientSearching(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchClients(clientSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearchTerm, searchClients]);

  // Key under which the currently-selected override client is remembered, so a
  // page refresh keeps you on the same client with its saved overrides shown
  // instead of resetting to nothing (which looked like the save had failed).
  const CLIENT_OVERRIDE_SELECTION_KEY = "expirationSettings.selectedOverrideClient";

  const loadClientOverrides = async (client: any) => {
    if (!client?.id) return;
    setClientLoading(true);
    const res = await getClientExpirationOverrides(client.id);
    setClientOverrides(res?.overrides || {});
    setClientLoading(false);
  };

  const handleSelectClient = async (client: any) => {
    setSelectedClient(client);
    setClientSearchTerm("");
    setClientResults([]);
    setClientOverrides({});
    setClientDirty(false);
    if (!client?.id) return;
    try {
      window.localStorage.setItem(
        CLIENT_OVERRIDE_SELECTION_KEY,
        JSON.stringify({ id: client.id, name: client.name }),
      );
    } catch (e) {
      // localStorage may be unavailable; persistence is best-effort.
    }
    await loadClientOverrides(client);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setClientOverrides({});
    setClientDirty(false);
    setClientSearchTerm("");
    setClientResults([]);
    try {
      window.localStorage.removeItem(CLIENT_OVERRIDE_SELECTION_KEY);
    } catch (e) {
      // best-effort
    }
  };

  // On mount, restore the last-selected override client (if any) so refreshing
  // does not appear to lose saved overrides.
  useEffect(() => {
    let stored: { id: string; name: string } | null = null;
    try {
      const raw = window.localStorage.getItem(CLIENT_OVERRIDE_SELECTION_KEY);
      stored = raw ? JSON.parse(raw) : null;
    } catch (e) {
      stored = null;
    }
    if (stored?.id) {
      setSelectedClient(stored);
      loadClientOverrides(stored);
    }
    refreshOverrideClients();
  }, []);

  const handleClientOverrideChange = (key: string, value: any) => {
    setClientOverrides((prev: any) => {
      const next = { ...prev };
      if (value === "" || value === undefined || value === null) {
        delete next[key];
      } else {
        next[key] = Number(value);
      }
      return next;
    });
    setClientDirty(true);
  };

  const handleSaveClientOverrides = async () => {
    if (!selectedClient?.id) return;
    setClientSaving(true);
    const res = await setClientExpirationOverrides(selectedClient.id, clientOverrides);
    if (res?.success !== false) {
      toast.add("Client overrides saved for " + (selectedClient.name || "client"), "var(--theme-ui-colors-success)");
      setClientDirty(false);
      refreshOverrideClients();
    } else {
      toast.add(res?.message || "Failed to save overrides", "var(--theme-ui-colors-danger)");
    }
    setClientSaving(false);
  };

  if (loading) {
    return (
      <ExpirationShell embedded={embedded} subtitle="Configure quote and RFQ expiration behavior, per-client overrides, and admin actions.">
        <Box sx={{ padding: "40px", textAlign: "center", fontFamily: "Poppins", color: "base_500" }}>Loading...</Box>
      </ExpirationShell>
    );
  }

  if (!isAdmin) {
    return (
      <ExpirationShell embedded={embedded}>
        <Box sx={{ fontFamily: "Poppins", fontSize: "14px", color: "base_500", padding: "40px 0", textAlign: "center" }}>
          This page is for admin use only. Notification settings are available under Company Settings.
        </Box>
      </ExpirationShell>
    );
  }

  return (
    <ExpirationShell embedded={embedded} subtitle="Configure quote and RFQ expiration behavior, per-client overrides, and admin actions.">
        {/* Save Bar */}
        {dirty && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            <BasicButton
              scheme="primary"
              onClick={handleSave}
              $submitting={saving}
              $loadingColor="#fff"
              keepText={true}
              disabled={!dirty}
            >
              Save Changes
            </BasicButton>
          </div>
        )}
        {/* Feature Toggle */}
        <SectionCard title="Feature Toggle" description="Master switch for the quote expiration system.">
          <Flex sx={{ alignItems: "center", gap: "12px" }}>
            <Switch
              checked={!!formValues.isEnabled}
              onChange={() => handleChange("isEnabled", !formValues.isEnabled)}
              sx={{
                backgroundColor: formValues.isEnabled ? themevals.colors.primary : "#ccc",
                "& > div": { backgroundColor: "#fff" },
              }}
            />
            <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: 600, color: formValues.isEnabled ? themevals.colors.primary : "base_500" }}>
              {formValues.isEnabled ? "Enabled" : "Disabled"}
            </Box>
          </Flex>
          {!formValues.isEnabled && (
            <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: themevals.colors.warning, marginTop: "8px" }}>
              When disabled, no expiration dates are set on new quotes/RFQs and the expiration job skips all processing.
            </Box>
          )}
        </SectionCard>

        {/* Expiration Timing */}
        <SectionCard title="Expiration Timing" description="How long quotes and RFQs stay active before expiring.">
          <Grid columns={[1, 2, 3]} gap="20px">
            {["quoteExpirationDays", "rfqExpirationDays", "expirationWarningDays", "regenerationExpirationDays"].map((key) => {
              const field = FIELD_DEFS.find((f) => f.key === key);
              return field ? (
                <SettingsField key={key} fieldKey={key} label={field.label} value={formValues[key]} onChange={handleChange} description={FIELD_DESCRIPTIONS[key]} />
              ) : null;
            })}
          </Grid>
        </SectionCard>

        {/* Limits */}
        <SectionCard title="Limits" description="Controls on regeneration and extension frequency.">
          <Grid columns={[1, 2, 3]} gap="20px">
            {["maxRegenerations", "rfqExtensionDays", "maxRfqExtensions"].map((key) => {
              const field = FIELD_DEFS.find((f) => f.key === key);
              return field ? (
                <SettingsField key={key} fieldKey={key} label={field.label} value={formValues[key]} onChange={handleChange} description={FIELD_DESCRIPTIONS[key]} />
              ) : null;
            })}
          </Grid>
        </SectionCard>

        {/* Thresholds */}
        <SectionCard title="Thresholds & Price Lock" description="Value thresholds and price lock parameters.">
          <Grid columns={[1, 2, 3]} gap="20px">
            {["highValueThreshold", "priceLockDepositPercent", "priceLockMaxDays", "retroactiveGraceDays"].map((key) => {
              const field = FIELD_DEFS.find((f) => f.key === key);
              return field ? (
                <SettingsField key={key} fieldKey={key} label={field.label} value={formValues[key]} onChange={handleChange} description={FIELD_DESCRIPTIONS[key]} />
              ) : null;
            })}
          </Grid>
        </SectionCard>

        {/* Per-Client Overrides */}
        {isAdmin && (
        <SectionCard title="Per-Client Overrides" description="Set custom expiration settings for specific client organizations. Empty fields inherit from global defaults.">
          {/* Summary list of clients that already have overrides. Collapsed rows;
              click one to select it and expand the editor below. Keeps the page
              compact as more clients are configured. */}
          {overrideClients.length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 600, color: themevals.colors.base_500, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
                Clients with overrides ({overrideClients.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {overrideClients.map((oc: any) => {
                  const isSelected = selectedClient?.id === oc.clientId;
                  const count = oc.overrides ? Object.keys(oc.overrides).length : 0;
                  return (
                    <button
                      type="button"
                      key={oc.clientId}
                      onClick={() => handleSelectClient({ id: oc.clientId, name: oc.clientName })}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        textAlign: "left",
                        cursor: "pointer",
                        fontFamily: "Poppins, sans-serif",
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: "1px solid " + (isSelected ? themevals.colors.info : themevals.colors.base_300),
                        backgroundColor: isSelected ? "#F0F7FF" : "#FFFFFF",
                      }}
                    >
                      <span style={{ fontSize: "14px", fontWeight: 600, color: themevals.colors.base_800 }}>
                        {oc.clientName || "Unnamed Client"}
                      </span>
                      <span style={{ fontSize: "12px", color: themevals.colors.base_500 }}>
                        {count} override{count !== 1 ? "s" : ""}{isSelected ? " · editing" : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ position: "relative", marginBottom: "20px" }}>
            {selectedClient ? (
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  flex: 1,
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: themevals.colors.base_800,
                  padding: "8px 12px",
                  border: "1px solid " + themevals.colors.info,
                  borderRadius: "6px",
                  backgroundColor: "#F0F7FF",
                }}>
                  {selectedClient.name || "Unnamed Client"}
                </div>
                <button
                  type="button"
                  onClick={handleClearClient}
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    color: themevals.colors.base_500,
                    background: "none",
                    border: "1px solid " + themevals.colors.base_300,
                    borderRadius: "4px",
                    padding: "6px 12px",
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
                <BasicButton
                  scheme="primary"
                  onClick={handleSaveClientOverrides}
                  $submitting={clientSaving}
                  $loadingColor="#fff"
                  keepText={true}
                  disabled={!clientDirty}
                  styles={{
                    flexShrink: "0",
                    // BasicButton sets the disabled attribute but applies no
                    // disabled styling, so a disabled button looks identical to
                    // an enabled one. Dim it here when there is nothing to save
                    // so the state reads honestly.
                    opacity: clientDirty ? "1" : "0.5",
                    cursor: clientDirty ? "pointer" : "not-allowed",
                  }}
                >
                  {clientDirty ? "Save Overrides" : "Saved"}
                </BasicButton>
              </div>
            ) : (
              <div>
                <Label sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "6px", display: "block" }}>
                  Search Client
                </Label>
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  placeholder="Type client name to search..."
                  style={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid " + themevals.colors.base_300,
                    borderRadius: "6px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {clientSearching && (
                  <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: themevals.colors.base_500, marginTop: "4px" }}>
                    Searching...
                  </div>
                )}
                {clientResults.length > 0 && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid " + themevals.colors.base_300,
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    zIndex: 100,
                    maxHeight: "240px",
                    overflowY: "auto",
                  }}>
                    {clientResults.map((c: any) => (
                      <div
                        key={c.id}
                        onClick={() => handleSelectClient(c)}
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #F0F0F0",
                        }}
                        onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = "#F7F7F7"; }}
                        onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = "#fff"; }}
                      >
                        {c.name || (c.firstName ? c.firstName + " " + c.lastName : "Unnamed")}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {clientLoading && (
            <Box sx={{ fontFamily: "Poppins", fontSize: "13px", color: "base_500" }}>Loading overrides...</Box>
          )}
          {selectedClient && !clientLoading && (
            <Box>
              <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "12px" }}>
                Only set values you want to override for this client. Leave empty to use global defaults.
              </Box>
              <Grid columns={[1, 2]} gap="16px">
                {OVERRIDABLE_FIELDS.map((field) => (
                  <Box key={field.key}>
                    <Label sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "2px", display: "block" }}>
                      {field.label}
                    </Label>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginBottom: "6px" }}>
                      Default: {formValues[field.key] ?? "\u2014"}
                    </Box>
                    <Input
                      type="number"
                      value={clientOverrides[field.key] ?? ""}
                      onChange={(e) => handleClientOverrideChange(field.key, e.target.value)}
                      placeholder={"Leave empty to use default"}
                      sx={{ fontFamily: "Poppins", fontSize: "14px", border: "1px solid", borderColor: clientOverrides[field.key] ? "info" : "base_300", borderRadius: "6px", padding: "8px 12px", backgroundColor: clientOverrides[field.key] ? "#F0F7FF" : "#fff" }}
                      min={0}
                    />
                  </Box>
                ))}
              </Grid>
              {Object.keys(clientOverrides).length > 0 && (
                <Box sx={{ marginTop: "12px", fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.info }}>
                  {Object.keys(clientOverrides).length} override{Object.keys(clientOverrides).length !== 1 ? "s" : ""} set for {selectedClient.name || "this client"}
                </Box>
              )}
            </Box>
          )}
        </SectionCard>
        )}

        {/* Default Notification Preferences */}
        <SectionCard title="Default Notification Preferences" description="System-wide defaults for email notifications. All users inherit these unless they set their own preferences in My Settings.">
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {NOTIFICATION_DEFAULTS.map((notif) => {
              const isDigest = notif.key === "defaultDigestFrequency";
              const options = isDigest ? DIGEST_FREQUENCY_OPTIONS : FREQUENCY_OPTIONS;
              return (
                <div key={notif.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", backgroundColor: "#FAFAFA", borderRadius: "8px" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "#2D3748", marginBottom: "4px" }}>
                      {notif.label}
                    </div>
                    <div style={{ fontFamily: "Poppins", fontSize: "12px", color: "#718096", lineHeight: "1.4" }}>
                      {notif.description}
                    </div>
                  </div>
                  <div style={{ marginLeft: "16px", flexShrink: 0 }}>
                    <Select
                      value={formValues[notif.key] || "monthly"}
                      onChange={(e) => handleChange(notif.key, e.target.value)}
                      sx={{ fontFamily: "Poppins", fontSize: "13px", minWidth: "160px" }}
                    >
                      {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* Admin Actions */}
        <SectionCard title="Admin Actions" description="Manual triggers for expiration processing and data population.">
          <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
            <Box>
              <BasicButton
                scheme="secondary"
                onClick={handleRunJob}
                $submitting={runningJob}
                $loadingColor={themevals.colors.base_800}
                keepText={true}
              >
                Run Expiration Job
              </BasicButton>
              <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginTop: "4px" }}>
                Manually trigger the daily expiration check
              </Box>
            </Box>
            <Box>
              <BasicButton
                scheme="secondary"
                onClick={handleSendDigest}
                $submitting={sendingDigest}
                $loadingColor={themevals.colors.base_800}
                keepText={true}
              >
                Send Digest Emails
              </BasicButton>
              <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginTop: "4px" }}>
                Send expiration summary email to all companies with expiring items
              </Box>
            </Box>
          </Flex>

          <Box sx={{ borderTop: "1px solid", borderColor: "base_200", paddingTop: "20px", marginBottom: "20px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "8px" }}>
              Retroactive Population
            </Box>
            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "12px" }}>
              Set expiration dates on existing open records. Use the age cutoff to only affect recent records.
            </Box>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <label style={{ fontFamily: "Poppins", fontSize: "12px", color: "#4A5568", whiteSpace: "nowrap" }}>Only records from the last</label>
              <Input
                value={populateMaxAge}
                onChange={(e: any) => setPopulateMaxAge(e.target.value)}
                type="number"
                min={0}
                sx={{ fontFamily: "Poppins", fontSize: "13px", width: "80px", padding: "6px 10px" }}
              />
              <span style={{ fontFamily: "Poppins", fontSize: "12px", color: "#4A5568" }}>days</span>
              <span style={{ fontFamily: "Poppins", fontSize: "11px", color: "#A0AEC0" }}>(leave empty for all records)</span>
            </div>
            <Flex sx={{ gap: "12px" }}>
              <BasicButton
                scheme="secondary"
                outline={true}
                onClick={() => handlePopulate("grace")}
                $submitting={populating}
                $loadingColor={themevals.colors.base_800}
                keepText={true}
              >
                Populate (Grace)
              </BasicButton>
              <BasicButton
                scheme="secondary"
                outline={true}
                onClick={() => handlePopulate("realistic")}
                $submitting={populating}
                $loadingColor={themevals.colors.base_800}
                keepText={true}
              >
                Populate (Realistic)
              </BasicButton>
            </Flex>
            <Box sx={{ fontFamily: "Poppins", fontSize: "11px", color: "base_400", marginTop: "6px" }}>
              Grace = NOW + grace days (only NULL records). Realistic = createdAt + configured days (overwrites).
            </Box>
          </Box>

          <Box sx={{ borderTop: "1px solid", borderColor: "base_200", paddingTop: "20px" }}>
            <Box sx={{ fontFamily: "Poppins", fontSize: "13px", fontWeight: 600, color: "base_800", marginBottom: "8px" }}>
              Legacy Cleanup
            </Box>
            <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "base_500", marginBottom: "12px" }}>
              Close old open records that predate the expiration feature. These are silently expired and will NOT appear as action items requiring attention.
            </Box>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <label style={{ fontFamily: "Poppins", fontSize: "12px", color: "#4A5568", whiteSpace: "nowrap" }}>Close records older than</label>
              <Input
                value={legacyOlderThan}
                onChange={(e: any) => setLegacyOlderThan(e.target.value)}
                type="number"
                min={1}
                sx={{ fontFamily: "Poppins", fontSize: "13px", width: "80px", padding: "6px 10px" }}
              />
              <span style={{ fontFamily: "Poppins", fontSize: "12px", color: "#4A5568" }}>days</span>
            </div>
            <BasicButton
              scheme="dangerSecondary"
              onClick={handleCloseLegacy}
              $submitting={closingLegacy}
              $loadingColor={themevals.colors.base_800}
              keepText={true}
            >
              Close Legacy Records
            </BasicButton>
          </Box>
        </SectionCard>
    </ExpirationShell>
  );
}
